import { isExpired, parseEnvelope } from './envelope';

type StorageGetter = (key: string) => string | null;

function unwrapEnvelopeRaw(raw: string): string {
  const env = parseEnvelope(raw);
  if (!env) return raw;
  const v = (env as { value: unknown }).value;
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function wrapGetItem(storage: Storage): void {
  try {
    const originalGetItem = storage.getItem.bind(storage) as StorageGetter;
    storage.getItem = ((key: string) => {
      const raw = originalGetItem(key);
      if (raw === null) return null;
      const env = parseEnvelope(raw);
      if (env && isExpired(env)) {
        // Keep reads compatible but don't expose expired cookie-compliance values.
        try {
          storage.removeItem(key);
        } catch {
          // best-effort cleanup; safe to ignore failures here
        }
        return null;
      }
      return unwrapEnvelopeRaw(raw);
    }) as StorageGetter;
  } catch {
    // best-effort patch; if it fails, getItem falls back to native behavior
  }
}

/**
 * Compatibility normalization: unwrap TTL envelopes on plain getItem reads.
 *
 * Storage-compliance stores web storage values as JSON envelopes:
 * `{ value, _ts, _exp }`.
 *
 * Existing call sites that do `JSON.parse(localStorage.getItem(key) ?? '...')` expect the raw value.
 * This keeps them working by normalizing `getItem()` to return the unwrapped payload.
 */
export function normalizeWebStorageGetItem(): void {
  if (globalThis.window === undefined) return;
  wrapGetItem(globalThis.localStorage);
  wrapGetItem(globalThis.sessionStorage);
}
